import { useEffect, useRef, useState } from 'react';
import type { GameState } from '@/types';
import { playCountdown, playEventMessage } from '@/audio/rushAudio';
import './GrowReport.css';

const LABELS: Record<string, string> = {
  food: 'Food',
  energy: 'Energy',
  minerals: 'Minerals',
  oxygen: 'Oxygen',
  water: 'Water',
  helium3: 'Helium-3',
  hydrogen: 'Hydrogen',
};

interface Ledger {
  before?: Record<string, number>;
  used?: Record<string, number>;
  spoiled?: Record<string, number>;
  grew?: Record<string, number>;
  after?: Record<string, number>;
}

interface Report {
  playerId?: string;
  name?: string;
  before?: Record<string, number>;
  used?: Record<string, number>;
  spoiled?: Record<string, number>;
  grew?: Record<string, number>;
  after?: Record<string, number>;
  foodShort?: boolean;
  energyShort?: boolean;
  nextSeconds?: number;
}

function keysOf(report: Ledger) {
  const keys = new Set<string>();
  for (const bag of [report.before, report.used, report.spoiled, report.grew, report.after]) {
    for (const key of Object.keys(bag || {})) {
      const value = bag?.[key] || 0;
      if (value) keys.add(key);
    }
  }
  return [...keys];
}

function amount(bag: Record<string, number> | undefined, key: string) {
  const value = bag?.[key] || 0;
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export default function GrowReport({ game }: { game: GameState }) {
  const timer = game.productionTimer;
  const reports = (timer?.reports || []) as Report[];
  const messages = timer?.events?.messages || [];
  const duration = timer?.duration || 20;
  const [left, setLeft] = useState(duration);

  useEffect(() => {
    const start = Date.parse(timer?.startTime || '');
    const tick = () => {
      if (!Number.isFinite(start)) {
        setLeft(duration);
        return;
      }
      setLeft(Math.max(0, Math.ceil(duration - (Date.now() - start) / 1000)));
    };
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [timer?.startTime, duration]);

  const heardEvents = messages.join('\n');
  const heardEventsRef = useRef('');
  useEffect(() => {
    const previous = heardEventsRef.current;
    heardEventsRef.current = heardEvents;
    heardEvents.split('\n').filter((message) => message && !previous.includes(message)).forEach(playEventMessage);
  }, [heardEvents]);

  useEffect(() => {
    playCountdown(left);
  }, [left]);

  return (
    <div className="grow-report" role="status">
      <div className="grow-report-card">
        <header>
          <h2>Grow and use</h2>
          <span>Trading in {left}s</span>
        </header>
        {messages.map((message) => (
          <p key={message} className="grow-event">{message}</p>
        ))}
        {reports.map((report) => {
          const rows = keysOf(report);
          return (
            <section key={report.playerId || report.name}>
              <h3>{report.name}</h3>
              <p className={report.foodShort ? 'is-short' : ''}>
                {report.foodShort
                  ? `Food is short. Next cultivation is ${report.nextSeconds}s.`
                  : 'Food covers the next cultivation.'}
                {report.energyShort ? ' Energy is short for the mules.' : ''}
              </p>
              {rows.length > 0 && (
                <table>
                  <thead>
                    <tr>
                      <th>Resource</th>
                      <th>Had</th>
                      <th>Used</th>
                      <th>Spoiled</th>
                      <th>Grew</th>
                      <th>Now</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((key) => (
                      <tr key={key}>
                        <td>{LABELS[key] || key}</td>
                        <td>{amount(report.before, key)}</td>
                        <td>{amount(report.used, key)}</td>
                        <td>{amount(report.spoiled, key)}</td>
                        <td>{amount(report.grew, key)}</td>
                        <td>{amount(report.after, key)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
